import { Typography, TypographyProps } from '@mui/material';
import moment from 'moment';
import invariant from 'tiny-invariant';

import { IOptionalTimeWindow } from '../../types/datetimes';
import { NullableFormattedDatetime } from '../../utils/datetimes/formatting';
import { TentativeDateDisplay, VisitSingleDateDisplay } from '../dates/CommonDateDisplays';
import { OptionalDatetimeFulfillmentWindowDisplay } from '../fulfillmentWindows/OptionalDatetimeFulfillmentWindowDisplay';
import { CaptionLabel } from '../labels/CaptionLabel';
import { NoneLabel } from '../labels/NoneLabel';
import { isTimeWindowSingleDay } from '../queues/helpers';
import { VerticalStack } from '../utils/linearLayouts';

function VisitDatetimeDisplay({
    label = true,
    startDatetime,
    timezone,
    labelProps = {},
    datetimeProps = {},
}: {
    label?: boolean;
    startDatetime: Date;
    timezone: string;
    labelProps?: TypographyProps;
    datetimeProps?: TypographyProps;
}) {
    return (
        <VerticalStack>
            {label ? <CaptionLabel {...labelProps}>Visit Date</CaptionLabel> : null}
            <Typography {...datetimeProps}>
                <NullableFormattedDatetime datetime={moment(startDatetime)} timezone={timezone} />
            </Typography>
        </VerticalStack>
    );
}

export const VisitDateTypeDisplay = ({
    label = true,
    startDatetime,
    effectiveDate,
    tentativeDate,
    fulfillmentWindow,
    timezone,
    fallbackDate,
    labelProps = {},
    datetimeProps = {},
    displayFulfillmentWindow = true,
}: {
    label?: boolean;
    startDatetime: Date | null;
    effectiveDate: Date | null;
    tentativeDate: Date | null;
    fulfillmentWindow: IOptionalTimeWindow | null;
    timezone: string;
    fallbackDate: Date | null;
    labelProps?: TypographyProps;
    datetimeProps?: TypographyProps;
    displayFulfillmentWindow?: boolean;
}) => {
    if (startDatetime) {
        return (
            <VisitDatetimeDisplay
                label={label}
                startDatetime={startDatetime}
                timezone={timezone}
                labelProps={labelProps}
                datetimeProps={datetimeProps}
            />
        );
    }
    if (effectiveDate) {
        return (
            <VisitSingleDateDisplay
                label={label}
                startDate={effectiveDate}
                timezone={timezone}
                labelProps={labelProps}
                datetimeProps={datetimeProps}
            />
        );
    }
    if (fulfillmentWindow && isTimeWindowSingleDay(fulfillmentWindow)) {
        invariant(fulfillmentWindow.startDatetime);
        return (
            <VisitSingleDateDisplay
                label={label}
                startDate={fulfillmentWindow.startDatetime}
                timezone={timezone}
                labelProps={labelProps}
                datetimeProps={datetimeProps}
            />
        );
    }
    if (tentativeDate) {
        return (
            <TentativeDateDisplay
                tentativeDate={tentativeDate}
                label={label}
                dateProps={datetimeProps}
            />
        );
    }
    if (displayFulfillmentWindow && fulfillmentWindow) {
        return (
            <OptionalDatetimeFulfillmentWindowDisplay
                fulfillmentWindow={fulfillmentWindow}
                timezone={timezone}
                label={label}
                dateProps={datetimeProps}
            />
        );
    }

    // NOTE: THis fallback is a legacy piece of data included in the legacy
    //  rest api. It is set by either the date of the visit's start datetime or
    //  from the date expressed with visit preferences iff those preferences are
    //  all contained in a single day. This was used to express what is now tentative
    //  date, but there exists data still that uses this method of expressing date
    //  rather than tentative date.
    if (fallbackDate) {
        return (
            <VisitDatetimeDisplay
                label={label}
                startDatetime={fallbackDate}
                timezone={timezone}
                datetimeProps={datetimeProps}
            />
        );
    }

    return <NoneLabel {...datetimeProps} />;
};
